// packages/web/src/components/RecordActions.tsx — R2: generic record actions,
// driven by the type's schema. Renders (a) the LIFECYCLE transitions legal
// from the record's CURRENT state as calm buttons (POST /records/:id/
// transition; the server stays source of truth — an illegal transition 422s
// lifecycle_guard, surfaced calmly here), OR — for a type with a plain
// status-only picklist and no lifecycle — an inline status <select> (PATCH
// /records/:id); and (b) RETIRE with a confirm step (POST /records/:id/
// archive). R3 reuses this on every detail screen.
//
// S10 — NOTHING IS DELETED (Huan, 2026-08-12; wf02 §01b: "A tech who leaves
// keeps their history and their payroll. Same principle as the receipt:
// nothing is deleted, things are reversed or retired with a record of it").
// This component used to offer a generic Delete on EVERY record type — tech
// profiles, visits, work orders, all of it — which contradicted that rule
// everywhere at once. S4 and S9 both refused to work around it; the ruling
// closes it here, in the one shared place, so no screen has to remember.
//
// RETIRE IS GATED WHERE DELETE WAS NOT. POST /records/:id/archive arms the
// authority gate (records.ts's "ARCHIVE — gated, not cascaded"), so on a
// pack with an authority manifest a caller without the archive action gets
// C5e's 409 offer instead of the change. That is handled the same way every
// other converting surface in this app handles it — read the offer with
// authorityOfferFrom, file it with fileAskFromOffer, show the receipt —
// never a dead button and never a client-side guess about who may retire.
// A refusal that carries no fileable offer says so plainly instead of
// pretending; it does NOT fall back to delete.
import { useState } from 'preact/hooks';
import {
  ApiError,
  archiveRecord,
  transitionRecord,
  updateRecord,
  type RecordRow,
  type RecordTypeSchema,
} from '../lib/api';
import { convertRefusalToAsk } from '../lib/authorityGate';
import './RecordActions.css';

interface RecordActionsProps {
  tenantId: string;
  schema: RecordTypeSchema;
  record: RecordRow;
  onChanged: (record: RecordRow) => void;
  /** S10: was `onDeleted`. The record is archived, not removed — callers
   * refresh (and usually close) exactly as they did before. */
  onRetired: (record: RecordRow) => void;
  /** ⚑ THE EDIT SHEET'S UNSAVED FIELDS (Day 2 walk, S3.14).
   *
   * This block and `RecordForm` are two components and ONE sheet to the person
   * reading them. They typed the outcome, pressed Passed, and nothing moved —
   * the transition carried the record as loaded, so the server was asked to
   * make a move whose required field was sitting on the screen unsaved. The
   * answer was a refusal about a field the person had just filled in, and on
   * the walk it did not even reach them.
   *
   * The form publishes what it is holding into this ref; the move sends it.
   * Absent — every other surface that renders this block without a form —
   * behaves exactly as it always did. */
  pendingFields?: { current: Record<string, unknown> };
}

/** 'confirm' is the deliberate second step the act has always had; the three
 * terminal states after it are the same vocabulary TechDetail's ask and
 * verify use, for the same reason — the outcome is the receipt. */
type RetireState = 'idle' | 'confirm' | 'busy' | 'requested' | 'refused';

function humanizeValue(v: string): string {
  return v
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

export function RecordActions({ tenantId, schema, record, onChanged, onRetired, pendingFields }: RecordActionsProps) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retire, setRetire] = useState<RetireState>('idle');

  // A "status" picklist field with no lifecycle is the inline-status case
  // (e.g. actor_payment.status: owed/received/overdue).
  const statusField = schema.lifecycle
    ? undefined
    : schema.fields.find((f) => f.name === 'status' && f.type === 'picklist');

  /** One sentence for a failed write, and it converts first.
   *
   * The 409 authority offer does NOT wear the `{error:{message}}` envelope, so
   * `err.message` for one is the literal string "request failed" — the exact
   * dead end ruling A1 exists to abolish. So: try to turn it into an ask, and
   * only fall back to the server's own sentence when it was never an offer. */
  async function refusalLine(err: unknown, fallback: string): Promise<string> {
    const converted = await convertRefusalToAsk(tenantId, err);
    if (converted?.outcome === 'filed') {
      return 'Not yours to make — it became an ask. Whoever can say yes has it, and it is in your Asks.';
    }
    if (converted) return `Not yours to make. ${converted.message}`;
    return err instanceof ApiError ? err.message : fallback;
  }

  async function doTransition(to: string) {
    setBusy(true);
    setError(null);
    try {
      const updated = await transitionRecord(tenantId, record.id, to, pendingFields?.current);
      onChanged(updated);
    } catch (err) {
      // ⚑ A STATE CHANGE IS A WRITE, SO IT CAN BE GATED TOO (Day 3 walk, item
      // 2). A child moving her own audition to Callback gets the same 409
      // offer a create does, and this line used to render it as the literal
      // string "request failed". A 422 lifecycle_guard (or any other error) is
      // still surfaced calmly in the server's words, never thrown.
      setError(await refusalLine(err, 'Could not change the state.'));
    } finally {
      setBusy(false);
    }
  }

  async function doStatus(value: string) {
    setBusy(true);
    setError(null);
    try {
      const updated = await updateRecord(tenantId, record.id, { status: value });
      onChanged(updated);
    } catch (err) {
      setError(await refusalLine(err, 'Could not update status.'));
    } finally {
      setBusy(false);
    }
  }

  /** The attempt IS the mechanism (the same shape TechDetail's ask and
   * ticket verify use): a holder's click retires; a non-holder's click is
   * converted by the server's 409 into C5e's signed offer and filed. */
  async function doRetire() {
    setRetire('busy');
    setError(null);
    try {
      const archived = await archiveRecord(tenantId, record.id);
      onRetired(archived);
    } catch (err) {
      // ⚑ ONE HELPER (Day 3 walk, item 2). This conversion was written here
      // and again in WorkOrderDrawer, and the generic record form — the door a
      // child actually uses — had neither. It lives in lib/authorityGate.ts
      // now; this surface keeps its own three-state button.
      const converted = await convertRefusalToAsk(tenantId, err);
      if (converted?.outcome === 'filed') {
        setRetire('requested');
      } else if (converted?.outcome === 'no-road') {
        setRetire('refused');
      } else if (converted?.outcome === 'ask-failed') {
        // The gate DID offer a road; filing it is what failed. Back to the
        // button, in the server's own sentence — a refusal that cannot change
        // gets no retry, and this one can.
        setRetire('confirm');
        setError(converted.message);
      } else {
        setRetire('confirm');
        setError(err instanceof ApiError ? err.message : 'Could not retire this.');
      }
    }
  }

  const nextStates =
    schema.lifecycle && record.lifecycle_state
      ? schema.lifecycle.transitions[record.lifecycle_state] ?? []
      : [];
  const anyBusy = busy || retire === 'busy';

  return (
    <div class="ra">
      {schema.lifecycle && (
        <div class="ra__section">
          <div class="ra__label">
            State · <span class="ra__now" data-testid="ra-state">{humanizeValue(record.lifecycle_state ?? '—')}</span>
          </div>
          {nextStates.length === 0 ? (
            <div class="ra__hint">No further transitions.</div>
          ) : (
            <div class="ra__buttons">
              {nextStates.map((to) => (
                <button
                  type="button"
                  key={to}
                  class="ra__btn"
                  disabled={anyBusy}
                  onClick={() => doTransition(to)}
                  data-testid={`ra-to-${to}`}
                >
                  {humanizeValue(to)}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {statusField && (
        <div class="ra__section">
          <label class="ra__label" for="ra-status">
            Status
          </label>
          <select
            id="ra-status"
            class="ra__select"
            disabled={anyBusy}
            value={(record.fields['status'] as string) ?? ''}
            onChange={(e) => doStatus((e.target as HTMLSelectElement).value)}
            data-testid="ra-status"
          >
            {(statusField.picklistValues ?? []).map((opt) => (
              <option value={opt} key={opt}>
                {humanizeValue(opt)}
              </option>
            ))}
          </select>
        </div>
      )}

      <div class="ra__section ra__section--retire">
        {retire === 'requested' ? (
          <div class="ra__hint" role="status" data-testid="ra-retire-requested">
            ✓ Requested — sent to the back office
          </div>
        ) : retire === 'refused' ? (
          <div class="ra__hint" role="status" data-testid="ra-retire-refused">
            Retiring this is outside your authority, and the office can't be asked automatically for it.
          </div>
        ) : retire === 'idle' ? (
          <button
            type="button"
            class="ra__btn ra__btn--ghost"
            disabled={anyBusy}
            onClick={() => setRetire('confirm')}
            data-testid="ra-retire"
          >
            Retire
          </button>
        ) : (
          <div class="ra__confirm">
            <span class="ra__hint">
              Retire this {schema.label.toLowerCase()}? It stops being active. Its history stays.
            </span>
            <button
              type="button"
              class="ra__btn"
              disabled={anyBusy}
              onClick={doRetire}
              data-testid="ra-retire-confirm"
            >
              {retire === 'busy' ? 'Retiring…' : 'Retire'}
            </button>
            <button
              type="button"
              class="ra__btn ra__btn--ghost"
              disabled={anyBusy}
              onClick={() => {
                setRetire('idle');
                setError(null);
              }}
            >
              Keep active
            </button>
          </div>
        )}
      </div>

      {error && (
        <div class="ra__error" role="alert" data-testid="ra-error">
          {error}
        </div>
      )}
    </div>
  );
}
